import { signal } from '@preact/signals';
import type { AccountView, AdminData, AdminPlayer, DeathCard, HeroId, SelfView, SquadMember, RealmPhase, DuelPhase, SpectateTarget } from '@knight/shared';

export type Screen = 'home' | 'heroes' | 'squad' | 'game' | 'profile' | 'guild' | 'settings' | 'leaderboards' | 'pets' | 'vault' | 'build';

export interface RealmInfo {
  code: string;
  phase: RealmPhase;
  corruption: number;
  players: number;
  boss: string;
  closeIn: number;
}

export interface DuelInfo {
  code: string;
  phase: DuelPhase;
  round: number;
  wins: number[];
  names: string[];
  until: number;
  winner?: string;
}

export interface FeedItem {
  id: number;
  text: string;
  at: number;
  color?: string;
}

export interface ChatItem {
  id: number;
  from: string;
  text: string;
  at: number;
  system?: boolean;
  spectator?: boolean;
}

export const screen = signal<Screen>('home');
export const account = signal<AccountView | null>(null);
export const conn = signal<'offline' | 'connecting' | 'online' | 'error'>('connecting');
export const offlineMode = signal(false);
export const squad = signal<{ code: string; members: SquadMember[] } | null>(null);
export const realm = signal<RealmInfo | null>(null);
export const duel = signal<DuelInfo | null>(null);
export const self = signal<SelfView | null>(null);
export const deathCard = signal<DeathCard | null>(null);
export const feed = signal<FeedItem[]>([]);
export const chat = signal<ChatItem[]>([]);
export const announce = signal<{ text: string; color?: string; at: number } | null>(null);
export interface ToastItem {
  id: number;
  text: string;
  at: number;
  kind?: 'hint' | 'warn';
}
/** short notices, newest last; at most three on screen */
export const toasts = signal<ToastItem[]>([]);

export interface ConfirmReq {
  title?: string;
  text: string;
  ok?: string;
  cancel?: string;
  danger?: boolean;
}
/** the pending in-app confirmation, if any */
export const confirmReq = signal<(ConfirmReq & { id: number }) | null>(null);
let confirmResolve: ((v: boolean) => void) | null = null;
let confirmId = 1;

/** Asks the player; resolves false when dismissed. Replaces the browser's blocking confirm. */
export function confirmDialog(req: ConfirmReq): Promise<boolean> {
  if (confirmResolve) confirmResolve(false);
  return new Promise((resolve) => {
    confirmResolve = resolve;
    confirmReq.value = { ...req, id: confirmId++ };
  });
}

export function answerConfirm(v: boolean): void {
  const fn = confirmResolve;
  confirmResolve = null;
  confirmReq.value = null;
  fn?.(v);
}
export const queue = signal<'idle' | 'searching' | 'found'>('idle');
export const bossBar = signal<{ name: string; hp: number; mhp: number } | null>(null);
export const inGame = signal(false);
export const gameMode = signal<'realm' | 'duel'>('realm');
export const heroChoice = signal<HeroId>((localStorage.getItem('knight.hero') as HeroId) || 'revenant');
/** account hero slot the next realm uses */
export const slotChoice = signal<number>(Math.max(0, parseInt(localStorage.getItem('knight.slot') ?? '0', 10) || 0));
/** bumped whenever offline heroes change so screens re-read localStorage */
export const heroesTick = signal(0);
/** owner debug console (F8) */
export const adminOpen = signal(false);
export const admin = signal<{ enabled: boolean; players: AdminPlayer[]; realm?: AdminData['realm'] }>({ enabled: false, players: [] });
export const chatOpen = signal(false);
export const buildOpen = signal(false);
export const menuOpen = signal(false);
export const pendingAvenge = signal<string | null>(null);
export const pendingSquad = signal<string | null>(null);
export const pendingRealm = signal<string | null>(null);
/** nickname from a #/w/ link: watch once connected */
export const pendingWatch = signal<string | null>(null);
/** show mode: who this session is watching (null while playing) */
export const spectate = signal<SpectateTarget | null>(null);
/** current fame prices of crowd effects against the watched hero */
export const crowdPrices = signal<Record<string, number> | null>(null);
/** performance.now() of the viewer's last crowd purchase (15 s cooldown) */
export const crowdLastBuy = signal(0);
export interface StickView {
  ox: number;
  oy: number;
  dx: number;
  dy: number;
}
/** touch sticks, mirrored from the input layer so the HUD can draw them */
export const stickMove = signal<StickView | null>(null);
export const stickAim = signal<StickView | null>(null);
/** the leave cast: set while the hero is channelling their way out of a realm */
export const leaving = signal<{ sec: number; at: number } | null>(null);
/** hero slot open in the setup screen */
export const buildSlot = signal(0);
export const googleReady = signal<boolean>(false);
export const ping = signal(0);

let feedId = 1;
export function pushFeed(text: string, color?: string): void {
  const list = [...feed.value, { id: feedId++, text, at: Date.now(), color }];
  feed.value = list.slice(-8);
}

let chatId = 1;
export function pushChat(from: string, text: string, system = false, spectator = false): void {
  chat.value = [...chat.value, { id: chatId++, from, text, at: Date.now(), system, spectator }].slice(-60);
}

let toastId = 1;
export function showToast(text: string, kind?: ToastItem['kind']): void {
  const id = toastId++;
  toasts.value = [...toasts.value, { id, text, at: Date.now(), kind }].slice(-3);
  setTimeout(() => {
    toasts.value = toasts.value.filter((x) => x.id !== id);
  }, kind === 'hint' ? 5200 : 2800);
}

export function showAnnounce(text: string, color?: string): void {
  announce.value = { text, color, at: Date.now() };
  setTimeout(() => {
    if (announce.value && Date.now() - announce.value.at >= 3900) announce.value = null;
  }, 4000);
}

export function setHero(h: HeroId): void {
  heroChoice.value = h;
  localStorage.setItem('knight.hero', h);
}

export function setSlot(i: number): void {
  slotChoice.value = i;
  localStorage.setItem('knight.slot', String(i));
}

export function resetGameState(): void {
  realm.value = null;
  duel.value = null;
  self.value = null;
  deathCard.value = null;
  bossBar.value = null;
  feed.value = [];
  buildOpen.value = false;
  chatOpen.value = false;
  menuOpen.value = false;
  spectate.value = null;
  crowdPrices.value = null;
  leaving.value = null;
  stickMove.value = null;
  stickAim.value = null;
}
